import React, {useEffect} from "react";

/**
 * Public campaign information page.
 *
 * ⚠ The dates and ticket values here are the published campaign terms and are
 *   mirrored in three other places — config/campaign.js, the API's
 *   LOTTERY_ITERATIONS (happy-pay-cust.php) and the job's
 *   (HappyPayLoyaltyJobController.php). Change one and change all four, or the
 *   page will promise something the system does not do.
 */

/**
 * The two draws, in the order they happen.
 *
 * A draw whose schedule is not settled carries `pending` instead of dates. The
 * card then promises detail later rather than a date that may move — a date
 * published here is a date customers will hold the bank to.
 */
const DRAWS = [
    {
        title: 'Тохирол 1',
        date: '2026.09.04',
        collects: '2026.08.11 - 2026.09.03-ны хооронд цуглуулсан эрхээр оролцоно.',
    },
    {
        title: 'Тохирол 2',
        date: '2026.10.09',
        collects: '2026.09.07 - 2026.10.08-ны хооронд цуглуулсан эрхээр оролцоно.',
    },
];

/** How tickets are earned. `tickets` is the headline number on each row. */
const WAYS_TO_EARN = [
    {
        icon: '💳',
        title: 'Happy Pay-д Visa картаа холбоод',
        tickets: '5 эрх',
        notes: [
            'Хөтөлбөрийн хугацаанд нэг л удаа боломжтой байна.',
            'Өмнө нь Visa картаа холбоод ашиглаж байсан хэрэглэгчдийн эрх шууд нэмэгдэнэ.',
        ],
    },
    {
        icon: '👥',
        title: 'Найзаа уриад',
        tickets: '3 эрх',
        notes: [
            'Happy Pay-р найзаа уриад амжилттай бүртгүүлсэн тохиолдолд эрх олгогдоно.',
        ],
    },
    {
        icon: '💸',
        title: '3 гүйлгээ тутамд',
        tickets: '1 эрх',
        notes: [
            'Happy pay-р хийсэн бүх төрлийн гүйлгээ хамаарна.',
        ],
    },
];

/**
 * The campaign window, or null to withhold it.
 *
 * Null for now: the window closes on the same 2026.10.15 as the second draw, so
 * publishing it here would publish the very date the Тохирол 2 card withholds.
 * Restore the string once the schedule is settled — '2026.08.11 - 2026.10.15'.
 */
const CAMPAIGN_PERIOD = null;

const CAUTIONS = [
    'Сугалаанд оролцохын тулд дээрх 3 нөхцөлөөс тус бүрийг дор хаяж нэг удаа биелүүлсэн байх шаардлагатай.',
    'Зохиомол байдлаар гүйлгээгээ нэмэгдүүлсэн тохиолдолд хөтөлбөрт хамрагдах боломжгүй байна.',
    'Зөвхөн Happy Pay app-аар хийгдсэн гүйлгээг тооцно.',
];

const SectionHeading = ({icon, children}) => (
    <h2 className="text-xl mobile:text-lg font-semibold text-[#0987F3]">
        {icon && <span aria-hidden="true" className="mr-2">{icon}</span>}
        {children}
    </h2>
);

const InfoBodyHappyPay = () => {
    useEffect(() => {
        document.title = "Happy Pay";
    }, []);

    return (
        <div className="h-fit w-full bg-slate-200 px-40 mobile:px-0 laptop:px-20">
            <div className="h-full w-full bg-white">
                <img
                    className='w-full object-cover h-full mobile:hidden'
                    src="./happy_pay_2026/3000x1110.jpg"
                    alt="banner_thin"
                />

                <img
                    className='w-full object-cover h-full hidden mobile:block'
                    src="./happy_pay_2026/1080x2185.jpg"
                    alt="banner_thin_mobile"
                />

                {/* text-left: App.css sets `.App { text-align: center }` globally,
                    which would otherwise centre every heading and bullet here. */}
                <article className="w-full px-36 py-12 space-y-10 mobile:px-5 mobile:py-8 tablet:px-16 text-gray-700 text-left">

                    {/* Lead — the offer in one sentence. */}
                    <p className="text-xl mobile:text-base leading-relaxed text-center max-w-3xl mx-auto">
                        Happy Pay цахим хэтэвчээ ашиглан World's NO.1 K-POP Awards болох{' '}
                        <span className="font-semibold text-[#0987F3]">MAMA AWARDS</span>-г VIP эрхээр үзэх{' '}
                        <span className="font-semibold text-[#0987F3]">сүүлийн 6 азтаны</span> нэг болоорой!
                    </p>

                    {/* ===== Dates ===== */}
                    <section className="space-y-4">
                        <SectionHeading>Хөтөлбөрийн хугацаа</SectionHeading>

                        {CAMPAIGN_PERIOD && (
                            <p className="text-2xl mobile:text-xl font-bold text-gray-900 tabular-nums">
                                {CAMPAIGN_PERIOD}
                            </p>
                        )}
                        <p>Нийт 2 удаагийн тохирлоор тус бүр 6 азтан, нийт 12 азтан тодруулна.</p>

                        <div className="grid grid-cols-2 gap-4 mobile:grid-cols-1">
                            {DRAWS.map(draw => (
                                <div
                                    key={draw.title}
                                    className="rounded-xl border border-blue-200 bg-blue-50/60 p-5 space-y-2"
                                >
                                    <p className="text-sm font-semibold uppercase tracking-wide text-[#0987F3]">
                                        {draw.title}
                                    </p>
                                    {draw.pending ? (
                                        <p className="text-xl mobile:text-lg font-semibold text-gray-700">
                                            {draw.pending}
                                        </p>
                                    ) : (
                                        <>
                                            <p className="text-2xl mobile:text-xl font-bold text-gray-900 tabular-nums">
                                                {draw.date}
                                            </p>
                                            <p className="text-sm text-gray-600">{draw.collects}</p>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                        <p className="text-sm text-gray-600">
                            <span aria-hidden="true">📍 </span>
                            Тохирлыг банкны албан ёсны Facebook хуудсаар шууд дамжуулан тодруулна.
                        </p>
                    </section>

                    {/* ===== How to take part ===== */}
                    <section className="space-y-4">
                        <SectionHeading icon="✅">Урамшуулалт хөтөлбөрт хэрхэн оролцох вэ?</SectionHeading>

                        <ul className="list-none p-0 m-0 space-y-3">
                            {WAYS_TO_EARN.map(way => (
                                <li
                                    key={way.title}
                                    className="rounded-xl border border-gray-200 p-5 space-y-3"
                                >
                                    <div className="flex items-start justify-between gap-4 mobile:flex-col mobile:gap-2">
                                        <p className="font-semibold text-gray-900">
                                            <span aria-hidden="true" className="mr-2">{way.icon}</span>
                                            {way.title}
                                        </p>
                                        {/* Amber for a ticket, the same language the dashboard uses. */}
                                        <span className="shrink-0 rounded-full bg-[#FFF6E0] border border-[#F2D9A0] px-3 py-1 text-sm font-bold text-[#8A5A00] whitespace-nowrap">
                                            {way.tickets}
                                        </span>
                                    </div>
                                    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                                        {way.notes.map(note => <li key={note}>{note}</li>)}
                                    </ul>
                                </li>
                            ))}
                        </ul>
                    </section>

                    {/* ===== Cautions ===== */}
                    <section className="space-y-3 rounded-xl border border-gray-300 bg-gray-50 p-6 mobile:p-4">
                        <SectionHeading icon="🎯">Анхаарах зүйл</SectionHeading>
                        <ul className="list-disc pl-5 space-y-2 text-sm">
                            {CAUTIONS.map(caution => <li key={caution}>{caution}</li>)}
                        </ul>
                    </section>
                </article>
            </div>
        </div>
    );
};

export default InfoBodyHappyPay;
